import type { ReactNode } from 'react';

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';

import { Heading, Text } from './Typography';

interface PageSectionProps {
    title?: ReactNode;
    description?: ReactNode;
    action?: ReactNode;
    footer?: ReactNode;
    children: ReactNode;
    className?: string;
    contentClassName?: string;
    headerClassName?: string;
    footerClassName?: string;
}

export default function PageSection({
    title,
    description,
    action,
    footer,
    children,
    className,
    contentClassName,
    headerClassName,
    footerClassName,
}: PageSectionProps) {
    return (
        <Card
            className={cn(
                'overflow-hidden rounded-[calc(var(--radius)+0.25rem)] border border-border/80 bg-card/95 shadow-elevation-1',
                className,
            )}
        >
            {(title || description || action) && (
                <CardHeader
                    className={cn(
                        'flex flex-col gap-4 border-b border-border/70 pb-5 sm:flex-row sm:items-start sm:justify-between',
                        headerClassName,
                    )}
                >
                    <div className="space-y-1.5">
                        {title && (
                            <Heading as="h2" variant="section">
                                {title}
                            </Heading>
                        )}
                        {description && (
                            <Text variant="description" className="max-w-3xl">
                                {description}
                            </Text>
                        )}
                    </div>
                    {action && <div className="shrink-0">{action}</div>}
                </CardHeader>
            )}
            <CardContent className={cn('p-6', contentClassName)}>{children}</CardContent>
            {footer && (
                <div
                    className={cn(
                        'border-t border-border/70 bg-surface-muted/70 px-6 py-4',
                        footerClassName,
                    )}
                >
                    {footer}
                </div>
            )}
        </Card>
    );
}
